import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import useResourceOverviewToolbarContext from '~/modules/resource-management/ResourceManagementDashboard/hooks/useResourceManagementDashboardContext';
import { OVERVIEW_UNITS_ENUM } from '~/modules/resource-management/common/enums';
import { CHART_KEYS, DECIMAL_PLACES } from '../../hooks/data-conversion';

const unitLabelTranslationKeys = {
  [OVERVIEW_UNITS_ENUM.FTE]:
    'resourceManagementDashboard.benchOverAllocationTracker.resourcesFTE',
  [OVERVIEW_UNITS_ENUM.PERCENTAGE]:
    'resourceManagementDashboard.benchOverAllocationTracker.percentage',
  [OVERVIEW_UNITS_ENUM.HOURS]:
    'resourceManagementDashboard.benchOverAllocationTracker.hours'
};

export const tooltipTranslationKeys = {
  [CHART_KEYS.BENCH]:
    'resourceManagementDashboard.benchOverAllocationTracker.benchTooltipValue',
  [CHART_KEYS.NOT_WORKING]:
    'resourceManagementDashboard.benchOverAllocationTracker.notWorkingTooltipValue',
  [CHART_KEYS.OVER_ALLOCATION]:
    'resourceManagementDashboard.benchOverAllocationTracker.overAllocatedTooltipValue',
  [CHART_KEYS.FITTING_ALLOCATION]:
    'resourceManagementDashboard.benchOverAllocationTracker.fittingAllocationTooltipValue',
  [CHART_KEYS.TOTAL_RESOURCES]:
    'resourceManagementDashboard.benchOverAllocationTracker.totalResourcesTooltipValue'
};

export const TOOLTIP_FIELDS = [
  CHART_KEYS.BENCH,
  CHART_KEYS.NOT_WORKING,
  CHART_KEYS.FITTING_ALLOCATION,
  CHART_KEYS.TOTAL_RESOURCES,
  CHART_KEYS.OVER_ALLOCATION
];

const wrapText = (text, maxChars) => {
  if (!text || text.length <= maxChars) return [text];

  return text
    .match(/\S+\s*/g)
    .reduce(
      (lines, word) => {
        const currentLine = lines[lines.length - 1];

        if (word.length + currentLine.length > maxChars) {
          return [...lines, word];
        }

        return [...lines.slice(0, -1), currentLine + word];
      },
      ['']
    )
    .flatMap(line =>
      line.length > maxChars
        ? line.match(new RegExp(`.{1,${maxChars}}`, 'g'))
        : line
    );
};

const getUnitLabel = ({ formatMessage, displayUnit }) =>
  formatMessage({ id: unitLabelTranslationKeys[displayUnit] });

const getFormattedTooltipField = ({ formatMessage }) => ({
  key,
  value,
  unitLabel
}) =>
  formatMessage(
    { id: tooltipTranslationKeys[key] },
    { value: `${Number(value).toFixed(DECIMAL_PLACES)} ${unitLabel}` }
  );

export const useTooltipLabels = ({ availabilityData, maxChars }) => {
  const { displayUnit } = useResourceOverviewToolbarContext();
  const { formatMessage } = useIntl();

  const unitLabel = getUnitLabel({ formatMessage, displayUnit });

  const getTooltipLabels = useCallback(
    ({ datum }) => {
      const chartData = availabilityData.find(
        item => item.label.toString() === datum.x
      );

      if (!chartData) return datum.x;

      const yAxisLabel = chartData.label.join(' → ');
      const getFormattedTooltipFieldLabel = getFormattedTooltipField({
        formatMessage
      });

      const formattedTooltipFieldLabels = TOOLTIP_FIELDS.map(key =>
        getFormattedTooltipFieldLabel({
          key,
          value: chartData[key],
          unitLabel
        })
      );

      const formattedYAxisLabel = wrapText(yAxisLabel, maxChars);

      return [...formattedYAxisLabel, ...formattedTooltipFieldLabels].join(
        '\n'
      );
    },
    [availabilityData, formatMessage, maxChars, unitLabel]
  );

  return getTooltipLabels;
};
